import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PartnerDirectory = ({ searchQuery, category, status }) => {
  const [selectedPartner, setSelectedPartner] = useState(null);

  const partners = [
    {
      id: 1,
      name: 'Global Airport Services',
      category: 'airport-pickup',
      status: 'active',
      rating: 4.8,
      locations: ['New York', 'London', 'Tokyo', 'Dubai'],
      services: ['Airport Pickup', 'VIP Transfer', '24/7 Support'],
      pricing: '$45-120/trip',
      responseTime: '15 min',
      bookings: 1247,
      satisfaction: 96
    },
    {
      id: 2,
      name: 'SecureTravel Protection',
      category: 'security',
      status: 'active',
      rating: 4.9,
      locations: ['Global Coverage'],
      services: ['Executive Protection', 'Risk Assessment', 'Emergency Response'],
      pricing: '$200-500/day',
      responseTime: '5 min',
      bookings: 856,
      satisfaction: 98
    },
    {
      id: 3,
      name: 'Elite Dining Network',
      category: 'restaurant',
      status: 'active',
      rating: 4.7,
      locations: ['Paris', 'Milan', 'New York', 'Hong Kong'],
      services: ['Fine Dining', 'Corporate Events', 'Private Chef'],
      pricing: '$50-300/person',
      responseTime: '30 min',
      bookings: 2134,
      satisfaction: 94
    },
    {
      id: 4,
      name: 'MediCare International',
      category: 'healthcare',
      status: 'active',
      rating: 4.9,
      locations: ['Worldwide Network'],
      services: ['Medical Consultation', 'Emergency Care', 'Health Insurance'],
      pricing: '$100-500/visit',
      responseTime: '10 min',
      bookings: 645,
      satisfaction: 99
    },
    {
      id: 5,
      name: 'Premium Airport Concierge',
      category: 'airport-pickup',
      status: 'pending',
      rating: 4.5,
      locations: ['Los Angeles', 'Miami', 'Chicago'],
      services: ['Meet & Greet', 'Fast Track', 'Lounge Access'],
      pricing: '$30-80/service',
      responseTime: '20 min',
      bookings: 423,
      satisfaction: 91
    },
    {
      id: 6,
      name: 'SafeGuard Security Solutions',
      category: 'security',
      status: 'suspended',
      rating: 4.2,
      locations: ['Europe', 'Middle East'],
      services: ['Travel Security', 'Threat Intelligence', 'Crisis Management'],
      pricing: '$150-400/day',
      responseTime: '15 min',
      bookings: 312,
      satisfaction: 87
    }
  ];

  const filteredPartners = partners?.filter(partner => {
    const matchesSearch = partner?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         partner?.services?.some(s => s?.toLowerCase()?.includes(searchQuery?.toLowerCase()));
    const matchesCategory = category === 'all' || partner?.category === category;
    const matchesStatus = status === 'all' || partner?.status === status;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10 border-success/20';
      case 'pending': return 'text-warning bg-warning/10 border-warning/20';
      case 'suspended': return 'text-error bg-error/10 border-error/20';
      case 'maintenance': return 'text-muted-foreground bg-muted/10 border-muted/20';
      default: return 'text-muted-foreground bg-muted/10 border-muted/20';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'airport-pickup': return 'Plane';
      case 'security': return 'Shield';
      case 'restaurant': return 'UtensilsCrossed';
      case 'healthcare': return 'Heart';
      case 'accommodation': return 'Hotel';
      default: return 'Building2';
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Building2" size={24} className="text-primary" />
            <span className="text-2xl font-bold text-foreground">{partners?.length}</span>
          </div>
          <p className="text-sm text-muted-foreground">Total Partners</p>
        </div>
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CheckCircle" size={24} className="text-success" />
            <span className="text-2xl font-bold text-foreground">{partners?.filter(p => p?.status === 'active')?.length}</span>
          </div>
          <p className="text-sm text-muted-foreground">Active Partners</p>
        </div>
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="MapPin" size={24} className="text-accent" />
            <span className="text-2xl font-bold text-foreground">45+</span>
          </div>
          <p className="text-sm text-muted-foreground">Global Locations</p>
        </div>
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Star" size={24} className="text-warning" />
            <span className="text-2xl font-bold text-foreground">4.7</span>
          </div>
          <p className="text-sm text-muted-foreground">Avg Rating</p>
        </div>
      </div>
      {/* Partners Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredPartners?.map((partner) => (
          <div
            key={partner?.id}
            className="glass-container rounded-xl border border-border/50 p-6 hover:shadow-glass-lg transition-all duration-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <Icon name={getCategoryIcon(partner?.category)} size={24} color="white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">{partner?.name}</h3>
                  <div className="flex items-center space-x-2 mt-1">
                    <Icon name="Star" size={14} className="text-warning" />
                    <span className="text-sm font-medium text-foreground">{partner?.rating}</span>
                    <span className="text-sm text-muted-foreground">({partner?.bookings} bookings)</span>
                  </div>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-lg border text-xs font-medium ${getStatusColor(partner?.status)}`}>
                {partner?.status?.charAt(0)?.toUpperCase() + partner?.status?.slice(1)}
              </div>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{partner?.locations?.join(', ')}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {partner?.services?.map((service, idx) => (
                  <span key={idx} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
                    {service}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4 pt-4 border-t border-border/30">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Pricing</p>
                <p className="text-sm font-medium text-foreground">{partner?.pricing}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Response</p>
                <p className="text-sm font-medium text-foreground">{partner?.responseTime}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Satisfaction</p>
                <p className="text-sm font-medium text-success">{partner?.satisfaction}%</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" iconName="Eye" className="flex-1">
                View Details
              </Button>
              <Button variant="default" size="sm" iconName="Calendar" className="flex-1">
                Book Service
              </Button>
            </div>
          </div>
        ))}
      </div>
      {filteredPartners?.length === 0 && (
        <div className="glass-container rounded-xl border border-border/50 p-12 text-center">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No partners found</h3>
          <p className="text-muted-foreground">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

export default PartnerDirectory;